import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ExternalLink, 
  Github, 
  Code, 
  Palette, 
  Zap, 
  Users, 
  Award,
  ArrowRight,
  Filter,
  Search,
  Star,
  Calendar,
  Globe
} from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 60 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: "easeOut" }
};

const staggerChildren = {
  animate: {
    transition: {
      staggerChildren: 0.15
    }
  }
};

const Projects = () => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const projects = [
    {
      id: 1,
      title: "Anon Ecommerce Website",
      category: "E-commerce",
      description: "A modern, responsive online store featuring fashion, accessories, perfumes, and more. Built with clean HTML/CSS, showcasing trending products, deals, and a user-friendly layout. Free shipping over $55 included!",
      url: "https://anonecomercewebsite.netlify.app/",
      image: "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=800",
      tech: ["HTML", "CSS", "Javascript"],
      status: "Live",
      year: "2024",
      rating: 5
    },
    {
      id: 2,
      title: "CleverCode",
      category: "Software Development",
      description: "Collaborated on software applications, improving user experience and functionality. Contributed to troubleshooting, debugging, and client interactions for tailored solutions.",
      url: "https://clevercode.pages.dev/",
      image: "https://images.pexels.com/photos/574071/pexels-photo-574071.jpeg?auto=compress&cs=tinysrgb&w=800",
      tech: ["JavaScript", "CSS3", "HTML5"],
      status: "Live",
      year: "2024",
      rating: 5
    },
    {
      id: 3,
      title: "Dream Makers Hub",
      category: "Education",
      description: "Created an engaging interface with interactive features and a curated course library to enhance learning experiences for students and professionals.",
      url: "https://dreammakershub.pages.dev/",
      image: "https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=800",
      tech: ["React", "Node.js", "MongoDB"],
      status: "Live",
      year: "2024",
      rating: 5
    },
    {
      id: 4,
      title: "Halloween Landing Page",
      category: "Education",
      description: "A fully responsive, animated Halloween website built with HTML, CSS, and JavaScript. Features a spooky product showcase, swiper sliders, discount sections, newsletter signup, and playful design perfect for seasonal promotions or themed events.",
      url: "https://halloweenwebsites.netlify.app",
      image: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSlmgIWJdpx04Ne5IMV1LCD4DHA3ealzzhMyw&s",
      tech: ["HTML", "CSS", "Javascript"],
      status: "Live",
      year: "2023",
      rating: 5
    },
    {
      id: 5,
      title: "TechScholar",
      category: "Education",
      description: "Built an intuitive interface, curated tech tutorials, and fostered community engagement through forums and mentorship programs for aspiring developers.",
      url: "https://www.usetechscholar.com/",
      image: "https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&w=800",
      tech: ["Next.js", "React", "PostgreSQL"],
      status: "Live",
      year: "2023",
      rating: 5
    },
    {
      id: 6,
      title: "SkinCure",
      category: "Healthcare",
      description: "Designed a sleek Skincure website with seamless WhatsApp and Instagram links, enabling instant customer connection and boosting brand engagement.",
      url: "https://skin-cure-website.netlify.app/",
      image: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTEV4Mi9qiEA5ftPydyNsi0XBeM9Ej-_Hnuqg&s",
      tech: ["Netlify", " Tailwind CSS", "React"],
      status: "Live",
      year: "2023",
      rating: 4
    }
,
    {
      id: 6,
      title: "Tasty",
      category: "Restaurant",
      description: "This is a fully responsive restaurant website designed for a fictional brand called Tasty. Built with HTML5, CSS3, and Boxicons, the website features a clean and modern layout optimized for mobile and desktop viewing. Key sections include Home, About, Services, Menu, and Contact Us, with a smooth scroll navigation and a dark/light theme toggle for enhanced user experience. The design highlights engaging visuals, strong branding, and user-friendly calls to action, making it ideal for showcasing restaurant or food-related content.",
      url: "https://tastwebsites.netlify.app/",
      image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?fm=jpg&q=60&w=3000&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8cmVzdGF1cmFudHxlbnwwfHwwfHx8MA%3D%3D",
      tech: ["HTML5", "CSS3", "JavaScript"],
      status: "Live",
      year: "2023",
      rating: 4
    },
    {
      id: 6,
      title: "StopShop",
      category: "Business",
      description: "I built this responsive landing page using React and Framer Motion to promote a platform that helps everyday people start their own tech-powered shops. The site includes smooth animations, a clean layout, and sections like Home, About, How It Works, and a call-to-action to get started. The focus was on creating an engaging user experience with modern web tools.",
      url: "https://stop-shop.netlify.app/",
      image: "https://static.vecteezy.com/system/resources/thumbnails/046/023/859/small_2x/real-estate-brokers-offer-attractive-promotions-customers-so-they-can-decide-purchase-condominium-unit-for-living-and-as-value-added-asset-concept-of-signing-real-estate-purchase-contract-with-dealer-photo.jpg",
      tech: ["Framer-Motion", " Tailwind CSS", "React"],
      status: "Live",
      year: "2023",
      rating: 4
    }
  ];

  const categories = ['all', 'E-commerce', 'Software Development', 'Education', 'Healthcare'];

  const filteredProjects = projects.filter(project => {
    const matchesFilter = filter === 'all' || project.category === filter;
    const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl animate-pulse delay-2000"></div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        
        {/* Header Section */}
        <motion.section 
          className="text-center mb-20"
          initial="initial"
          animate="animate"
          variants={staggerChildren}
        >
          <motion.div
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-8"
            variants={fadeInUp}
          >
            <Code className="w-4 h-4 text-purple-400" />
            <span className="text-white font-medium text-sm">Portfolio Showcase</span>
          </motion.div>

          <motion.h1
            className="text-5xl md:text-7xl font-bold mb-8 leading-tight"
            variants={fadeInUp}
          >
            <span className="text-white">My </span>
            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
              Projects
            </span>
          </motion.h1>

          <motion.p
            className="text-xl md:text-2xl text-slate-300 mb-12 max-w-3xl mx-auto leading-relaxed"
            variants={fadeInUp}
          >
            A collection of projects that showcase my passion for creating beautiful, functional web experiences
          </motion.p>

          {/* Stats */}
          <motion.div 
            className="grid grid-cols-3 gap-8 max-w-lg mx-auto mb-16"
            variants={fadeInUp}
          >
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-2">6+</div>
              <div className="text-slate-300 text-sm">Projects</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-2">100%</div>
              <div className="text-slate-300 text-sm">Success Rate</div>
            </div>
            <div className="text-center">
              <div className="flex justify-center mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-current text-yellow-400" />
                ))}
              </div>
              <div className="text-slate-300 text-sm">Client Rating</div>
            </div>
          </motion.div>
        </motion.section>

        {/* Filter and Search Section */}
        <motion.section 
          className="mb-16"
          initial="initial"
          animate="animate"
          variants={staggerChildren}
        >
          <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
            
            {/* Search Bar */}
            <motion.div 
              className="relative flex-1 max-w-md"
              variants={fadeInUp}
            >
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl pl-12 pr-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </motion.div>

            {/* Category Filter */}
            <motion.div 
              className="flex gap-2 flex-wrap"
              variants={fadeInUp}
            >
              {categories.map((category) => (
                <motion.button
                  key={category}
                  onClick={() => setFilter(category)}
                  className={`px-4 py-2 rounded-xl font-medium text-sm transition-all duration-300 ${
                    filter === category
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                      : 'bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {category === 'all' ? 'All Projects' : category}
                </motion.button>
              ))}
            </motion.div>
          </div>
        </motion.section>

        {/* Projects Grid */}
        <motion.section
          initial="initial"
          animate="animate"
          variants={staggerChildren}
        >
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                className="group bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl overflow-hidden hover:bg-white/15 transition-all duration-500"
                variants={fadeInUp}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.02 }}
              >
                {/* Project Image */}
                <div className="relative overflow-hidden h-48">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  
                  {/* Status Badge */}
                  <div className="absolute top-4 left-4">
                    <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                      <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                      {project.status}
                    </span>
                  </div>

                  {/* Year Badge */}
                  <div className="absolute top-4 right-4">
                    <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {project.year}
                    </span>
                  </div>

                  {/* Overlay with Links */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-4">
                    <motion.a
                      href={project.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white/20 backdrop-blur-sm p-3 rounded-full text-white hover:bg-white/30 transition-all duration-300"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <ExternalLink className="w-5 h-5" />
                    </motion.a>
                    <motion.button
                      className="bg-white/20 backdrop-blur-sm p-3 rounded-full text-white hover:bg-white/30 transition-all duration-300"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Github className="w-5 h-5" />
                    </motion.button>
                  </div>
                </div>

                {/* Project Content */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xl font-bold text-white group-hover:text-purple-300 transition-colors">
                      {project.title}
                    </h3>
                    <div className="flex">
                      {[...Array(project.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-current text-yellow-400" />
                      ))}
                    </div>
                  </div>

                  <div className="mb-4">
                    <span className="inline-block bg-purple-500/20 text-purple-300 px-3 py-1 rounded-full text-xs font-semibold">
                      {project.category}
                    </span>
                  </div>

                  <p className="text-slate-300 text-sm leading-relaxed mb-6 line-clamp-3">
                    {project.description}
                  </p>

                  {/* Tech Stack */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {project.tech.map((tech, techIndex) => (
                      <span 
                        key={techIndex}
                        className="bg-white/10 text-white px-2 py-1 rounded text-xs font-medium"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>

                  {/* Action Button */}
                  <motion.a
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group/btn inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-xl font-semibold text-sm hover:shadow-lg transition-all duration-300 w-full justify-center"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Globe className="w-4 h-4" />
                    Visit Project
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </motion.a>
                </div>
              </motion.div>
            ))}
          </div>

          {/* No Results Message */}
          {filteredProjects.length === 0 && (
            <motion.div 
              className="text-center py-20"
              variants={fadeInUp}
            >
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-12 max-w-md mx-auto">
                <Search className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">No Projects Found</h3>
                <p className="text-slate-300">Try adjusting your search or filter criteria</p>
              </div>
            </motion.div>
          )}
        </motion.section>

        {/* Call to Action */}
        <motion.section 
          className="text-center mt-20"
          initial="initial"
          animate="animate"
          variants={fadeInUp}
        >
          <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-sm border border-white/20 rounded-3xl p-12">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Like What You See?
            </h3>
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
              Let's collaborate and bring your next project to life with the same passion and attention to detail.
            </p>
            <motion.button
              className="group bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-2xl hover:shadow-purple-500/25 flex items-center gap-3 mx-auto"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <Users className="w-6 h-6" />
              Let's Work Together
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </motion.button>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default Projects;